<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';

const { t } = useI18n();
const { currentAccount, accountScopedRoute } = useAccount();
const { isAdmin } = useAdmin();

const accountAttributes = computed(
  () => currentAccount.value?.custom_attributes || {}
);

const trialEndsAt = computed(() => {
  const value = accountAttributes.value.yeszap_subscription_ends_on;
  if (!value) return null;

  const expiration = new Date(value);
  return Number.isNaN(expiration.getTime()) ? null : expiration;
});

const remainingDays = computed(() => {
  if (!trialEndsAt.value) return null;

  return Math.max(
    0,
    Math.ceil((trialEndsAt.value.getTime() - Date.now()) / 86400000)
  );
});

const isActiveTrial = computed(() => {
  return (
    accountAttributes.value.yeszap_subscription_status === 'trial' &&
    currentAccount.value?.status !== 'suspended' &&
    trialEndsAt.value?.getTime() > Date.now()
  );
});

const bannerMessage = computed(() => {
  if (remainingDays.value === 1) {
    return t('TRIAL_BANNER.ONE_DAY_LEFT');
  }

  return t('TRIAL_BANNER.DAYS_LEFT', { count: remainingDays.value });
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <section
    v-if="isActiveTrial"
    class="relative z-50 flex min-h-8 flex-shrink-0 items-center justify-center gap-3 bg-[#18d24b] px-3 py-1.5 text-xs font-medium text-[#082c16]"
  >
    <div class="flex min-w-0 items-center gap-1.5">
      <span class="i-lucide-clock-3 size-3.5 flex-shrink-0" />
      <span class="truncate sm:whitespace-normal">{{ bannerMessage }}</span>
    </div>
    <RouterLink
      v-if="isAdmin"
      :to="accountScopedRoute('billing_settings_index')"
      class="flex h-6 flex-shrink-0 items-center gap-1 rounded-md border border-[#082c16]/25 bg-white/35 px-2 font-semibold text-[#082c16] transition-colors hover:bg-white/55"
    >
      <span class="i-lucide-sparkles size-3.5" />
      <span class="hidden sm:inline">{{ t('TRIAL_BANNER.CHOOSE_PLAN') }}</span>
      <span class="sm:hidden">{{ t('TRIAL_BANNER.PLANS') }}</span>
    </RouterLink>
  </section>
</template>
